import { useEffect, useState } from 'react';
import { Dimensions, Platform } from 'react-native';

/**
 * `true` when we're on the web AND the viewport is wide enough to show
 * the desktop 3-zone shell (left nav rail + main + right glance rail).
 * Returns `false` on native — the phone tab-bar layout always wins there.
 *
 * Threshold is the point below which 180 + 720 + 260 + gutters stops
 * fitting comfortably. We pick 1024 because that gives the main column
 * its full ~720 even with right rail visible; anything tighter cramps.
 */
const DESKTOP_BREAKPOINT = 1024;

/** Read the current viewport width on web, preferring window.innerWidth
 *  over Dimensions.get('window') because the latter can return 0 on
 *  the very first static-export paint before RN-Web has measured the
 *  viewport. The 0 was making /welcome render in the mobile column
 *  on desktop refreshes — bumping isDesktop off → we'd snap to 440px
 *  width then never recover until a resize event fired. */
function readDesktop(): boolean {
  if (Platform.OS !== 'web') return false;
  if (typeof window !== 'undefined' && typeof window.innerWidth === 'number') {
    return window.innerWidth >= DESKTOP_BREAKPOINT;
  }
  return Dimensions.get('window').width >= DESKTOP_BREAKPOINT;
}

export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(readDesktop);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    // Re-read once after mount in case the initial paint happened
    // before the viewport reported real dimensions (rare but seen on
    // some refresh paths with static export + service worker).
    setIsDesktop(readDesktop());
    const sub = Dimensions.addEventListener('change', ({ window }) => {
      setIsDesktop(window.width >= DESKTOP_BREAKPOINT);
    });
    // Also listen to native window.resize on web for the same reason —
    // some browsers fire resize without firing the RN Dimensions event
    // on certain refresh / DPR-change paths.
    const onResize = () => setIsDesktop(readDesktop());
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onResize);
    }
    return () => {
      sub.remove();
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', onResize);
      }
    };
  }, []);

  return isDesktop;
}
